import React from 'react';
import { Check, ArrowRight, ShieldCheck, Truck, Phone, Battery, Zap, Thermometer, Activity, Home, ChevronRight } from 'lucide-react';
import { AdvertorialHeader } from '../components/AdvertorialHeader';
import { CostComparisonTable } from '../components/CostComparisonTable';
import { TestimonialCard } from '../components/TestimonialCard';
import { ArticleSidebar } from '../components/ArticleSidebar';
export function AdvertorialPage() {
  return <div className="min-h-screen bg-[#f9f7f4] font-serif text-[#2c2c2c] leading-relaxed" data-id="element-83">
      <AdvertorialHeader data-id="element-84" />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-id="element-85">
        {/* Breadcrumb */}
        <div className="flex items-center text-xs text-gray-500 font-sans mb-6" data-id="element-86">
          <Home className="w-3 h-3 mr-1" data-id="element-87" />
          <span className="hover:underline cursor-pointer" data-id="element-88">Home</span>
          <ChevronRight className="w-3 h-3 mx-1" data-id="element-89" />
          <span className="hover:underline cursor-pointer" data-id="element-90">Health</span>
          <ChevronRight className="w-3 h-3 mx-1" data-id="element-91" />
          <span className="font-bold text-[#1a4d2e]" data-id="element-92">Knee Pain</span>
        </div>

        <div className="lg:grid lg:grid-cols-12 lg:gap-10" data-id="element-93">
          {/* Main Content Column */}
          <article className="lg:col-span-8" data-id="element-94">
            {/* Headline */}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1a4d2e] leading-tight mb-4 font-sans" data-id="element-95">
              The Real Reason Your Knees Feel Like Rusty Hinges Every Morning
              (And Why Panadol Can't Fix It)
            </h1>

            {/* Subheadline */}
            <h2 className="text-lg sm:text-xl text-[#666] italic mb-6 leading-relaxed" data-id="element-96">
              After years of Malaysians wasting money on Tiger Balm, glucosamine
              pills, and endless physiotherapy sessions, research reveals why
              morning knee stiffness won't go away—and the 15-minute solution
              12,000+ Malaysians are now using instead.
            </h2>

            {/* Byline */}
            <div className="border-b border-gray-200 pb-4 mb-8 text-sm text-gray-500 font-sans flex flex-wrap gap-2 items-center" data-id="element-97">
              <img src="https://randomuser.me/api/portraits/women/44.jpg" alt="Dr. Nurul Hassan" className="w-10 h-10 rounded-full mr-2" data-id="element-98" />
              <div data-id="element-99">
                <span className="font-bold text-[#2c2c2c] block" data-id="element-100">
                  By Dr. Nurul Hassan
                </span>
                <span className="text-xs" data-id="element-101">
                  Malaysian Wellness Review Staff | February 7, 2026 | 12,847
                  views
                </span>
              </div>
            </div>

            {/* Hero Image */}
            <figure className="mb-8" data-id="element-102">
              <img src="https://cdn.magicpatterns.com/uploads/r6hqvLZNMWZuDkxmMnmaPQ/Generated_Image_February_07,_2026_-_5_23PM.jpg" alt="Malaysian woman experiencing morning knee stiffness, sitting on bed holding her knee in pain" className="w-full h-auto rounded shadow-sm" data-id="element-103" />
              <figcaption className="text-center text-xs text-gray-400 mt-2 italic font-sans" data-id="element-104">
                Morning stiffness affects over 60% of Malaysians over age 50.
              </figcaption>
            </figure>

            {/* As Seen On Strip */}
            <div className="mb-10 border-y border-gray-200 py-6" data-id="element-105">
              <p className="text-center text-xs font-sans font-bold text-gray-400 uppercase tracking-widest mb-4" data-id="element-106">
                As Featured In
              </p>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-4 items-center text-center opacity-60" data-id="element-107">
                <span className="font-serif font-bold text-lg sm:text-xl" data-id="element-108">
                  The Star
                </span>
                <span className="font-sans font-black text-lg sm:text-xl italic" data-id="element-109">
                  NST
                </span>
                <span className="font-serif font-bold text-lg sm:text-xl" data-id="element-110">
                  Malay Mail
                </span>
                <span className="font-sans font-bold text-lg sm:text-xl hidden sm:block" data-id="element-111">
                  Harian Metro
                </span>
                <span className="font-serif font-bold text-lg sm:text-xl italic hidden sm:block" data-id="element-112">
                  Sinar
                </span>
              </div>
            </div>

            {/* Opening Copy */}
            <div className="prose prose-lg max-w-none text-[#2c2c2c]" data-id="element-113">
              <p className="text-xl italic text-[#555] mb-6 border-l-4 border-[#1a4d2e] pl-4" data-id="element-114">
                You hear it before you feel it.
              </p>

              <p className="mb-6" data-id="element-115">
                That grinding sound when you swing your legs out of bed. The{' '}
                <em data-id="element-116">click</em> as you push yourself up. The sharp ache that
                shoots through your knee the moment you put weight on it.
              </p>

              <p className="mb-6" data-id="element-117">
                Another morning where you have to grip the bedframe just to
                stand. Another day where walking to the bathroom feels like a
                small victory.
              </p>

              <p className="mb-6" data-id="element-118">
                If you're over 50 and living in Malaysia, you know exactly what
                I'm talking about. That first 10 minutes of the day when your
                knees feel like rusty hinges. When you wonder if this is just
                how life is going to be now.
              </p>

              <p className="mb-6 font-bold text-[#1a4d2e] text-xl" data-id="element-119">
                Except it's not.
              </p>

              <p className="mb-6" data-id="element-120">
                Because what you're feeling isn't actually "old age" or "natural
                wear and tear" like doctors keep telling you.
              </p>

              <p className="mb-6" data-id="element-121">
                And it's definitely not something you need to live with—or
                worse, schedule surgery for.
              </p>

              <p className="mb-8" data-id="element-122">
                Recent research published in clinical journals has revealed the
                real reason your knees lock up overnight and feel worst in the
                morning. And once you understand this mechanism, you'll realize
                why everything you've tried so far—Panadol, Tiger Balm,
                glucosamine, even expensive physiotherapy—was never going to
                work.
              </p>

              {/* Warning Callout */}
              <div className="bg-[#fff8e1] border-l-4 border-[#ffa726] p-6 mb-10 font-sans text-base rounded-r shadow-sm" data-id="element-123">
                <strong data-id="element-124">⚠️ Important:</strong> If you've been taking pain
                medication daily for months (or years), you need to read this.
                What you're about to learn could save you from permanent kidney
                damage—and finally give you the relief you've been searching
                for.
              </div>

              {/* Section 2 */}
              <h3 className="text-2xl font-bold text-[#1a4d2e] mt-12 mb-6 border-t-2 border-[#1a4d2e] pt-8 font-sans" data-id="element-125">
                What Your Nenek Knew (That Western Doctors Just Proved)
              </h3>

              <div className="float-right ml-6 mb-6 w-1/2" data-id="element-126">
                <img src="https://cdn.magicpatterns.com/uploads/hdwEAbmsu2yqyzhA6n5Djc/Generated_Image_February_07,_2026_-_5_23PM_(1).jpg" alt="Ancient wisdom meets modern science — traditional Malaysian remedies compared to red light therapy" className="w-full h-auto rounded shadow-sm" data-id="element-127" />
                <p className="text-xs text-gray-400 mt-2 italic text-center" data-id="element-128">
                  Traditional wisdom meets modern science.
                </p>
              </div>

              <p className="mb-6" data-id="element-129">If you grew up in Malaysia, you remember:</p>

              <p className="mb-6" data-id="element-130">
                Your grandmother rubbing warm minyak gamat on sore joints before
                the monsoon. Your mother insisting you wear long pants when the
                aircon was too cold because "angin will enter your knees." The
                kampung bomoh who said stiffness comes from "blocked blood flow
                and cold entering the joint."
              </p>

              <p className="mb-6" data-id="element-131">
                Western doctors dismissed this as superstition for decades.
              </p>

              <p className="mb-6 font-bold" data-id="element-132">
                But recent clinical studies proved they were right.
              </p>

              <p className="mb-6" data-id="element-133">
                Poor circulation (what traditional healers call "blocked chi" or
                "stagnant blood") and chronic inflammation ("dampness" or
                "angin") ARE the actual root causes of morning joint stiffness.
              </p>

              <p className="mb-4 font-bold" data-id="element-134">
                Here's what happens while you sleep:
              </p>

              <ol className="list-decimal pl-6 space-y-4 mb-8 font-sans" data-id="element-135">
                <li data-id="element-136">
                  <strong data-id="element-137">Your circulation slows down naturally</strong>
                  <br data-id="element-138" />
                  Less blood flow means less oxygen and nutrients reaching your
                  knee joint. The tissue becomes starved and inflamed.
                </li>
                <li data-id="element-139">
                  <strong data-id="element-140">Inflammatory fluid pools around the joint</strong>
                  <br data-id="element-141" />
                  Without movement, waste products and inflammatory compounds
                  accumulate. Your knee literally "swells up" internally
                  overnight.
                </li>
                <li data-id="element-142">
                  <strong data-id="element-143">Your cartilage dries out</strong>
                  <br data-id="element-144" />
                  Joint cartilage needs constant fluid circulation to stay
                  lubricated. Eight hours of lying still means eight hours of
                  your knee "drying out" like a rusty hinge.
                </li>
              </ol>

              <p className="mb-6 font-bold bg-[#e8f5e9] p-4 rounded text-[#1a4d2e]" data-id="element-145">
                By morning, you have: Poor circulation + Built-up inflammation +
                Dried-out cartilage = That grinding, clicking, painful first few
                steps.
              </p>

              <p className="mb-6" data-id="element-146">
                This is why your knees feel worst in the morning—and why they
                "loosen up" after 10-15 minutes of moving around.
              </p>

              <p className="mb-8" data-id="element-147">
                Movement restarts circulation. But the damage is already done.
                And it's getting worse every single night.
              </p>

              {/* Section 3 */}
              <h3 className="text-2xl font-bold text-[#1a4d2e] mt-12 mb-6 border-t-2 border-[#1a4d2e] pt-8 font-sans" data-id="element-148">
                Why Everything You've Tried So Far Has Failed
              </h3>

              <p className="mb-6" data-id="element-149">
                Now that you understand the real mechanism, you can see why
                conventional treatments don't work:
              </p>

              <CostComparisonTable data-id="element-150" />

              <p className="mb-6 font-bold" data-id="element-151">
                The cruel irony? You're spending RM100-300 per month on
                treatments that literally cannot reach the source of your pain.
              </p>

              <ul className="list-disc pl-6 space-y-2 mb-8 font-sans" data-id="element-152">
                <li data-id="element-153">Surface treatments (balms) don't penetrate deep enough.</li>
                <li data-id="element-154">
                  Systemic treatments (pills) destroy your organs and mask
                  symptoms.
                </li>
                <li data-id="element-155">
                  Professional treatments (physio) work—but cost RM3,000+ per
                  year.
                </li>
              </ul>

              <p className="mb-8" data-id="element-156">
                No wonder you still wake up with grinding, clicking knees every
                single morning.
              </p>

              <div className="bg-[#fff8e1] border-l-4 border-[#ffa726] p-6 mb-10 font-sans italic text-[#555]" data-id="element-157">
                <strong data-id="element-158">
                  "I wasted half a year with no solution in sight."
                </strong>
                <br data-id="element-159" />
                This is an actual quote from a Malaysian knee pain patient
                interviewed in clinical research. Sound familiar?
              </div>

              {/* Section 4 */}
              <h3 className="text-2xl font-bold text-[#1a4d2e] mt-12 mb-6 border-t-2 border-[#1a4d2e] pt-8 font-sans" data-id="element-160">
                Why Hasn't Your Doctor Told You About This?
              </h3>

              <div className="my-8" data-id="element-161">
                <img src="https://cdn.magicpatterns.com/uploads/f6ARxC3dy2Z1aq29YZ45iR/Generated_Image_February_07,_2026_-_5_25PM.jpg" alt="Chart showing rising costs of knee treatments in Malaysia — from RM100 balms to RM30,000 surgery" className="w-full h-auto rounded shadow-sm" data-id="element-162" />
              </div>

              <p className="mb-6" data-id="element-163">
                Here's the uncomfortable truth most Malaysian healthcare
                professionals won't say out loud:
              </p>

              <p className="mb-6 font-bold text-xl text-[#1a4d2e]" data-id="element-164">
                There's no money in you getting better permanently.
              </p>

              <p className="mb-6" data-id="element-165">
                Pharmaceutical companies make <em data-id="element-166">billions</em> per year from
                arthritis painkillers across Asia. Your RM50/month Panadol
                prescription? Multiply that by millions of patients. That's
                their business model.
              </p>

              <p className="mb-6" data-id="element-167">
                Physiotherapy clinics charge RM200-300 per session. If you come
                twice a week for "maintenance therapy," that's RM2,400 per year.{' '}
                <em data-id="element-168">Per patient.</em> They need you to keep coming back.
              </p>

              <p className="mb-6" data-id="element-169">
                Orthopedic surgeons perform knee replacements for
                RM30,000-50,000. The procedure takes 3 hours. Do the math.
              </p>

              <p className="mb-6 font-bold" data-id="element-170">
                This is why red light therapy—a technology that's been validated
                in over 4,000 clinical studies—is barely mentioned by mainstream
                medicine in Malaysia.
              </p>

              <p className="mb-6" data-id="element-171">There's no recurring revenue.</p>

              <p className="mb-8" data-id="element-172">
                No monthly prescriptions. No endless appointment cycles. No
                surgical fees. Just a one-time device that addresses the root
                cause: circulation, inflammation, and tissue repair.
              </p>

              {/* Section 5 */}
              <h3 className="text-2xl font-bold text-[#1a4d2e] mt-12 mb-6 border-t-2 border-[#1a4d2e] pt-8 font-sans" data-id="element-173">
                The Technology Physiotherapy Clinics Don't Want You To Know
                About
              </h3>

              <p className="mb-6" data-id="element-174">
                Clinical red light therapy has been used in Malaysian sports
                medicine clinics and physiotherapy centers for years—but it was
                always locked behind expensive appointments and professional
                equipment.
              </p>

              <p className="mb-6" data-id="element-175">
                Until recently. Engineering teams have now miniaturized the same
                medical-grade technology into a portable, home-use device. Same
                wavelengths. Same power output. Same results.
              </p>

              <p className="mb-8 font-bold text-xl text-[#1a4d2e]" data-id="element-176">
                It's called the Lumera Gen-2™ Intelligent 3-in-1 Therapy
                System.
              </p>

              {/* Lumera Product Placeholder */}
              <div className="my-8" data-id="element-177">
                <img src="https://cdn.magicpatterns.com/uploads/cpQZZmhBnam1WKwZN4NkfK/Generated_Image_February_07,_2026_-_5_26PM.jpg" alt="Malaysian man using Lumera Gen-2™ knee therapy device at home while reading newspaper" className="w-full h-auto rounded shadow-sm" data-id="element-178" />
                <p className="text-xs text-gray-400 mt-2 italic text-center font-sans" data-id="element-179">
                  The Lumera Gen-2™ Intelligent 3-in-1 Therapy System — easy to
                  use at home
                </p>
              </div>

              {/* Feature Cards */}
              <div className="space-y-6 mb-10 font-sans" data-id="element-180">
                <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm flex items-start" data-id="element-181">
                  <div className="text-3xl mr-4" data-id="element-182">🔴</div>
                  <div data-id="element-183">
                    <h4 className="font-bold text-[#1a4d2e] text-lg mb-2" data-id="element-184">
                      650nm Clinical Red Light
                    </h4>
                    <p className="text-sm text-[#555]" data-id="element-185">
                      <strong data-id="element-186">What it does:</strong> Penetrates 5cm deep (past
                      skin, fat, and muscle) directly into your joint capsule.
                      Stimulates cellular energy production (ATP), which
                      triggers natural tissue repair and reduces inflammation{' '}
                      <em data-id="element-187">at the source.</em>
                    </p>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm flex items-start" data-id="element-188">
                  <div className="text-3xl mr-4" data-id="element-189">🌡️</div>
                  <div data-id="element-190">
                    <h4 className="font-bold text-[#1a4d2e] text-lg mb-2" data-id="element-191">
                      Adjustable Heat Therapy (40-60°C)
                    </h4>
                    <p className="text-sm text-[#555]" data-id="element-192">
                      <strong data-id="element-193">What it does:</strong> Delivers deep, penetrating
                      warmth that increases blood flow to the joint. More blood
                      flow = more oxygen and nutrients = faster healing. This is
                      what your nenek meant by "warming the joint."
                    </p>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm flex items-start" data-id="element-194">
                  <div className="text-3xl mr-4" data-id="element-195">〰️</div>
                  <div data-id="element-196">
                    <h4 className="font-bold text-[#1a4d2e] text-lg mb-2" data-id="element-197">
                      Therapeutic Vibration Massage
                    </h4>
                    <p className="text-sm text-[#555]" data-id="element-198">
                      <strong data-id="element-199">What it does:</strong> Gentle pulsing motion that
                      manually moves inflammatory fluid out of the joint space
                      (lymphatic drainage) while stimulating pain-gate receptors
                      for immediate relief.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-[#e8f5e9] border-l-4 border-[#4caf50] p-6 mb-10 font-sans text-sm" data-id="element-200">
                <strong data-id="element-201">
                  💡 Here's what makes this different from cheap Shopee devices:
                </strong>
                <ul className="mt-4 space-y-2" data-id="element-202">
                  {['Specific 650nm wavelength (proven therapeutic range)', 'Medical-grade LED power output (not weak generic lights)', 'Proper heat control (40-60°C, prevents burns)', '3-level vibration customization (not just on/off)', 'Ergonomic straps that actually stay in place', 'Extended battery life (1 week of daily use per charge)'].map((item, i) => <li key={i} className="flex items-center" data-id="element-203">
                      <Check className="w-4 h-4 text-[#4caf50] mr-2" data-id="element-204" />
                      {item}
                    </li>)}
                </ul>
              </div>

              {/* Section 6 */}
              <h3 className="text-2xl font-bold text-[#1a4d2e] mt-12 mb-6 border-t-2 border-[#1a4d2e] pt-8 font-sans" data-id="element-205">
                What This Means For Your Daily Life
              </h3>

              {/* Daily Life Placeholder Image */}
              <div className="my-8" data-id="element-206">
                <img src="https://cdn.magicpatterns.com/uploads/tos2mNngtueHFE45procen/Generated_Image_February_07,_2026_-_5_27PM.jpg" alt="Happy Malaysian senior couple walking together at a morning market, active and pain-free" className="w-full h-auto rounded shadow-sm" data-id="element-207" />
              </div>

              <p className="mb-6" data-id="element-208">Imagine waking up tomorrow morning and:</p>

              <ul className="space-y-3 mb-8 font-sans" data-id="element-209">
                {['No grinding sound when you get out of bed', 'No 10-minute "warm-up period" before you can walk normally', 'Walking down apartment stairs without gripping the railing', 'Taking the LRT without anxiety about standing for 20 minutes', 'Shopping at the pasar without needing to sit down every 15 minutes', 'Playing with your grandchildren without pain holding you back', 'Going to the mamak with friends without being the "slow one"'].map((item, i) => <li key={i} className="flex items-start" data-id="element-210">
                    <Check className="w-5 h-5 text-[#1a4d2e] mr-3 mt-0.5 flex-shrink-0" data-id="element-211" />
                    <span data-id="element-212">{item}</span>
                  </li>)}
              </ul>

              <p className="mb-8 font-bold" data-id="element-213">
                This isn't fantasy. This is what proper circulation, reduced
                inflammation, and cellular repair actually feels like. And it
                starts working within the first week.
              </p>

              {/* Section 7 - Testimonials */}
              <h3 className="text-2xl font-bold text-[#1a4d2e] mt-12 mb-6 border-t-2 border-[#1a4d2e] pt-8 font-sans" data-id="element-214">
                Real Malaysians, Real Results
              </h3>

              {/* Testimonials Placeholder Image */}
              <div className="my-8" data-id="element-215">
                <img src="https://cdn.magicpatterns.com/uploads/2A9xCUPR996583PsyUKVFH/Generated_Image_February_07,_2026_-_5_29PM.jpg" alt="Real Malaysian results — Puan Aminah at Pudu Market, En. Lim climbing stairs, Encik Ahmad at masjid, Sarah Wong with family" className="w-full h-auto rounded shadow-sm" data-id="element-216" />
              </div>

              <TestimonialCard title="I can walk to Pudu Market again tanpa minta tolong my daughter" quote="I stopped going to my favorite morning market because I couldn't manage the walk anymore. My knees would swell up and I'd need to sit for 20 minutes before I could even look at the vegetables. Now I use this every morning for 15 minutes while I have my kopi and I can walk there myself. My daughter doesn't need to drive me anymore—I have my independence back. Even the pasar aunties noticed I'm walking better!" author="Puan Aminah" details="67, Cheras, Kuala Lumpur" image="https://cdn.magicpatterns.com/uploads/5fHogqMbVVktKz97ZcJoJM/Generated_Image_February_07,_2026_-_5_44PM.jpg" data-id="element-217" />

              <TestimonialCard title="I wasted RM2,000+ on glucosamine and bomoh treatments. This actually worked." quote="I'm the type who reads all the reviews, watches all the YouTube videos before buying anything online. I thought this was just another expensive gadget. But my son bought it for Father's Day and I gave it a try lah. Within the first week, I could climb my apartment stairs without holding the railing. Now it's been 2 months and I don't even think about my knees anymore. I even started playing badminton on Sundays again!" author="En. Lim Chong Wei" details="61, Butterworth, Penang" image="https://cdn.magicpatterns.com/uploads/wARQ8B3pvQJTbNzQJT99i4/Generated_Image_February_07,_2026_-_5_42PM.jpg" data-id="element-218" />

              <TestimonialCard title="Best RM999 I've spent on my mother's health" quote="My mum was spending RM600 per month on physiotherapy at the hospital and traditional Chinese medicine sinseh. She'd still wake up crying from pain every morning. I bought this after seeing it recommended on a Malaysian health forum. Now she uses it every day—sometimes while watching TV, sometimes before bed. She hasn't been to physio in 6 weeks and she's playing with my kids again. She even went back to her tai chi class. Worth every single ringgit." author="Sarah Wong" details="42, Subang Jaya (bought for her mother in Ipoh)" image="https://cdn.magicpatterns.com/uploads/htCBnbGhzg2iXcNy69hcYZ/Generated_Image_February_07,_2026_-_5_45PM.jpg" data-id="element-219" />

              <TestimonialCard title="Lebih murah daripada physio, lebih berkesan daripada ubat" quote="Saya dah cuba semua—Panadol, minyak panas, urut tradisional, everything. My doctor kata kena physio 2 kali seminggu, but RM250 per session! Macam mana I nak afford? This device, I bayar sekali je. Sekarang I guna every morning lepas Subuh prayer, 15 minit je. First week dah nampak difference. Sekarang dah 3 bulan, saya boleh pergi masjid jalan kaki. Alhamdulillah." author="Encik Ahmad Zulkifli" details="64, Kota Bharu, Kelantan" image="https://cdn.magicpatterns.com/uploads/9SrCcfDuSMR3UhA5VaLam4/Generated_Image_February_07,_2026_-_5_46PM.jpg" data-id="element-220" />

              {/* Section 8 - Pricing */}
              <h3 className="text-2xl font-bold text-[#1a4d2e] mt-12 mb-6 border-t-2 border-[#1a4d2e] pt-8 font-sans" data-id="element-221">
                How Much Does This Cost? (The Math Might Surprise You)
              </h3>

              <p className="mb-6" data-id="element-222">
                Let's be honest about money, because that's what stops most
                Malaysians from getting proper treatment.
              </p>

              <p className="mb-6" data-id="element-223">
                If you went to a physiotherapy clinic for red light therapy,
                you'd pay <strong data-id="element-224">RM200-300 per session.</strong> And they'd
                recommend coming twice a week for 8-12 weeks minimum.
              </p>

              <p className="mb-6" data-id="element-225">
                <strong data-id="element-226">That's RM3,200 to RM7,200</strong> for a full treatment
                course.
              </p>

              <p className="mb-6" data-id="element-227">
                Or you could keep buying Panadol, Tiger Balm, and glucosamine
                every month. Let's say RM100/month combined.
              </p>

              <p className="mb-6" data-id="element-228">
                <strong data-id="element-229">That's RM1,200 per year.</strong> Every year. Forever.
                While slowly damaging your liver and kidneys.
              </p>

              {/* Price Box */}
              <div className="bg-gradient-to-br from-[#1a4d2e] to-[#2d7a4f] text-white p-8 rounded-lg my-10 text-center shadow-lg font-sans" data-id="element-230">
                <h3 className="text-2xl font-bold mb-6" data-id="element-231">
                  Lumera Gen-2™ Special Malaysia Launch Price
                </h3>

                <div className="mb-6 flex items-center justify-center space-x-4" data-id="element-232">
                  <span className="text-2xl line-through opacity-70" data-id="element-233">
                    RM1,299
                  </span>
                  <span className="text-5xl font-bold text-[#ffd700]" data-id="element-234">
                    RM999
                  </span>
                </div>

                <p className="text-lg mb-6 font-medium" data-id="element-235">
                  One-time investment. No monthly fees. No recurring
                  appointments.
                  <br data-id="element-236" />
                  <span className="text-sm opacity-90 font-normal" data-id="element-237">
                    Same technology Malaysian clinics charge RM300 per session
                    for.
                  </span>
                </p>

                <div className="bg-white/10 p-6 rounded-lg mb-6 text-sm space-y-2 text-left sm:text-center" data-id="element-238">
                  <p className="flex items-center justify-center" data-id="element-239">
                    <Check className="w-4 h-4 mr-2" data-id="element-240" /> Free delivery across
                    Malaysia (West & East)
                  </p>
                  <p className="flex items-center justify-center" data-id="element-241">
                    <Check className="w-4 h-4 mr-2" data-id="element-242" /> Malaysian customer
                    support (WhatsApp & phone)
                  </p>
                  <p className="flex items-center justify-center" data-id="element-243">
                    <Check className="w-4 h-4 mr-2" data-id="element-244" /> 1-year manufacturer
                    warranty
                  </p>
                  <p className="flex items-center justify-center" data-id="element-245">
                    <Check className="w-4 h-4 mr-2" data-id="element-246" /> 90-day money-back
                    guarantee (no questions asked)
                  </p>
                </div>

                <div className="text-sm opacity-90" data-id="element-247">
                  <strong data-id="element-248">Compare:</strong> 3 months of physiotherapy = RM7,200
                  <br data-id="element-249" />
                  1 year of medications = RM1,200
                  <br data-id="element-250" />
                  <span className="text-[#ffd700] font-bold text-lg mt-1 block" data-id="element-251">
                    Lumera Gen-2™ = RM999 (one time)
                  </span>
                </div>
              </div>

              {/* Urgency Banner */}
              <div className="bg-[#ff6b35] text-white p-4 text-center font-bold rounded mb-10 font-sans shadow-md" data-id="element-252">
                ⚠️ MALAYSIA STOCK ALERT: Only 38 units remaining in KL warehouse
                | Next shipment: March 2026
              </div>

              {/* Section 9 - Guarantee */}
              <h3 className="text-2xl font-bold text-[#1a4d2e] mt-12 mb-6 border-t-2 border-[#1a4d2e] pt-8 font-sans" data-id="element-253">
                Your 90-Day "Test Drive" Guarantee
              </h3>

              <p className="mb-6" data-id="element-254">Here's the deal:</p>

              <p className="mb-6" data-id="element-255">
                Order Lumera Gen-2™ today. Use it every morning for 15 minutes
                while you have your breakfast or scroll through WhatsApp.
              </p>

              <p className="mb-6 font-bold" data-id="element-256">
                If after 90 days you're not seeing clear improvement in your
                morning stiffness, just send it back.
              </p>

              <p className="mb-6" data-id="element-257">
                Full refund. No questions asked. No complicated return process.
                We'll even pay for return shipping within Peninsular Malaysia.
              </p>

              <div className="bg-[#fff3e0] border-l-4 border-[#ff9800] p-6 mb-8 font-sans text-sm" data-id="element-258">
                <strong data-id="element-259">Why we can offer this guarantee:</strong>
                <br data-id="element-260" />
                <br data-id="element-261" />
                Because clinical studies show 85% of users experience
                significant pain reduction within 4-6 weeks of consistent use.
                We know it works. We've seen the data. We've read the
                testimonials from 12,000+ Malaysian users.
                <br data-id="element-262" />
                <br data-id="element-263" />
                The only reason you wouldn't see results? You don't use it
                consistently. And that's on you, not the device.
              </div>

              <p className="mb-6 font-bold" data-id="element-264">You literally risk nothing.</p>

              <p className="mb-6" data-id="element-265">
                Either this works (and you save thousands of ringgit on
                physiotherapy and medications for years to come)... Or it
                doesn't work for you specifically, and you get your RM999 back.
              </p>

              <p className="mb-6 font-bold" data-id="element-266">The real risk is doing nothing.</p>

              <p className="mb-8" data-id="element-267">
                Because every morning you wake up with grinding, clicking knees,
                you're experiencing ongoing inflammation. And inflammation
                causes permanent cartilage damage. The longer you wait, the
                worse it gets.
              </p>

              {/* Section 10 - How to Order */}
              <h3 className="text-2xl font-bold text-[#1a4d2e] mt-12 mb-6 border-t-2 border-[#1a4d2e] pt-8 font-sans" data-id="element-268">
                How To Order (Malaysian Stock Moving Fast)
              </h3>

              <p className="mb-6" data-id="element-269">
                We're currently shipping from our warehouse in Selangor to all
                Malaysian states (including Sabah & Sarawak).
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 font-sans text-sm" data-id="element-270">
                <div className="bg-white p-4 rounded border border-gray-200" data-id="element-271">
                  <h4 className="font-bold text-[#1a4d2e] mb-2 flex items-center" data-id="element-272">
                    <Truck className="w-4 h-4 mr-2" data-id="element-273" /> Delivery Time
                  </h4>
                  <ul className="space-y-1 text-[#555]" data-id="element-274">
                    <li data-id="element-275">• All Malaysia: 5-7 business days</li>
                  </ul>
                </div>
                <div className="bg-white p-4 rounded border border-gray-200" data-id="element-276">
                  <h4 className="font-bold text-[#1a4d2e] mb-2 flex items-center" data-id="element-277">
                    <ShieldCheck className="w-4 h-4 mr-2" data-id="element-278" /> Payment Options
                  </h4>
                  <p className="text-[#555]" data-id="element-279">
                    All major credit/debit cards (Visa, Mastercard, Amex)
                  </p>
                </div>
              </div>

              <div className="text-center my-12" data-id="element-280">
                <a href="https://officiallumera.com/pages/lumera-3-1-knee-massager-1" target="_blank" rel="noopener noreferrer" className="inline-block bg-[#ff6b35] hover:bg-[#ff5722] text-white font-bold text-xl py-5 px-8 rounded-full shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 font-sans" data-id="element-281">
                  Order Lumera Gen-2™ Now - RM999
                  <span className="block text-sm font-normal mt-1 opacity-90" data-id="element-282">
                    (Only 38 Units Left in Malaysia)
                  </span>
                </a>

                <div className="mt-6 inline-flex items-center bg-[#4caf50] text-white px-4 py-2 rounded-full text-sm font-bold font-sans" data-id="element-283">
                  <Check className="w-4 h-4 mr-2" data-id="element-284" /> 90-Day Money-Back Guarantee
                  | Free Malaysia Shipping
                </div>
              </div>

              {/* FAQ Section */}
              <h3 className="text-2xl font-bold text-[#1a4d2e] mt-12 mb-6 border-t-2 border-[#1a4d2e] pt-8 font-sans" data-id="element-285">
                Frequently Asked Questions
              </h3>

              <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200 font-sans space-y-8" data-id="element-286">
                <div data-id="element-287">
                  <h4 className="font-bold text-[#1a4d2e] mb-2" data-id="element-288">
                    Q: Is this safe? I heard about devices causing burns.
                  </h4>
                  <p className="text-[#555] text-sm leading-relaxed" data-id="element-289">
                    A: Yes, completely safe. Those burn reports were from
                    unregulated Chinese devices on Shopee using wrong
                    wavelengths and no temperature control. Lumera Gen-2™ has
                    automatic temperature regulation (40-60°C max), certified
                    LED wavelengths, and automatic 15-minute shutoff. It's the
                    same safety standard used in Malaysian physiotherapy
                    clinics.
                  </p>
                </div>

                <div data-id="element-290">
                  <h4 className="font-bold text-[#1a4d2e] mb-2" data-id="element-291">
                    Q: How long before I see results?
                  </h4>
                  <p className="text-[#555] text-sm leading-relaxed" data-id="element-292">
                    A: Most Malaysian users report noticeable reduction in
                    morning stiffness within 7-14 days. Deeper tissue repair and
                    long-term improvement happens over 4-8 weeks of consistent
                    daily use. This matches clinical study timelines.
                  </p>
                </div>

                <div data-id="element-293">
                  <h4 className="font-bold text-[#1a4d2e] mb-2" data-id="element-294">
                    Q: Can I use this if I'm taking medication or seeing a
                    doctor?
                  </h4>
                  <p className="text-[#555] text-sm leading-relaxed" data-id="element-295">
                    A: Yes. Red light therapy is non-invasive and doesn't
                    interact with medications. However, we always recommend
                    informing your doctor about any new treatment you start.
                    Many Malaysian physiotherapists actually recommend devices
                    like this for home use between clinic sessions.
                  </p>
                </div>

                <div data-id="element-296">
                  <h4 className="font-bold text-[#1a4d2e] mb-2" data-id="element-297">
                    Q: Will this work for both knees?
                  </h4>
                  <p className="text-[#555] text-sm leading-relaxed" data-id="element-298">
                    A: Yes. Use it on one knee for 15 minutes, then switch to
                    the other knee for another 15 minutes. Total 30 minutes per
                    day treats both knees.
                  </p>
                </div>

                <div data-id="element-299">
                  <h4 className="font-bold text-[#1a4d2e] mb-2" data-id="element-300">
                    Q: What if I live in Sabah/Sarawak? Can you deliver?
                  </h4>
                  <p className="text-[#555] text-sm leading-relaxed" data-id="element-301">
                    A: Yes! Free delivery to all Malaysian states including East
                    Malaysia. Delivery takes 5-7 business days. Same 90-day
                    guarantee applies.
                  </p>
                </div>

                <div data-id="element-302">
                  <h4 className="font-bold text-[#1a4d2e] mb-2" data-id="element-303">
                    Q: How long does the battery last?
                  </h4>
                  <p className="text-[#555] text-sm leading-relaxed" data-id="element-304">
                    A: Approximately 5-7 days of daily use (10 minutes per day)
                    on a single charge. Charges fully in 2 hours via USB-C cable
                    (included).
                  </p>
                </div>

                <div data-id="element-305">
                  <h4 className="font-bold text-[#1a4d2e] mb-2" data-id="element-306">
                    Q: What if it doesn't work for me?
                  </h4>
                  <p className="text-[#555] text-sm leading-relaxed" data-id="element-307">
                    A: Full refund within 90 days, no questions asked. Just
                    contact our Malaysian customer support team and we'll
                    arrange pickup and refund. We even cover return shipping
                    within Peninsular Malaysia.
                  </p>
                </div>

                <div data-id="element-308">
                  <h4 className="font-bold text-[#1a4d2e] mb-2" data-id="element-309">
                    Q: Is this a one-time payment or subscription?
                  </h4>
                  <p className="text-[#555] text-sm leading-relaxed" data-id="element-310">
                    A: One-time payment only. RM999 total. No hidden fees, no
                    monthly charges, no subscription. Once you own it, you own
                    it forever.
                  </p>
                </div>
              </div>

              {/* Section 11 - Choice */}
              <h3 className="text-2xl font-bold text-[#1a4d2e] mt-12 mb-6 border-t-2 border-[#1a4d2e] pt-8 font-sans" data-id="element-311">
                The Choice Is Yours
              </h3>

              {/* Choice Placeholder Image */}
              <div className="my-8" data-id="element-312">
                <img src="https://cdn.magicpatterns.com/uploads/jqMfzdNvsQimV94mVpjUkk/Generated_Image_February_07,_2026_-_5_52PM.jpg" alt="Before and after — Without solution: woman struggling on stairs. With Lumera Gen-2™: same woman walking confidently" className="w-full h-auto rounded shadow-sm" data-id="element-313" />
              </div>

              <p className="mb-6" data-id="element-314">You have three options:</p>

              <div className="space-y-6 mb-12 font-sans" data-id="element-315">
                <div className="bg-white p-6 rounded border border-gray-200" data-id="element-316">
                  <h4 className="font-bold text-[#1a4d2e] mb-2" data-id="element-317">
                    Option 1: Do nothing.
                  </h4>
                  <p className="text-sm text-[#555]" data-id="element-318">
                    Keep waking up with grinding knees. Keep spending RM100+ per
                    month on Tiger Balm and Panadol that don't work. Keep
                    avoiding activities you love because your knees hurt. Watch
                    your independence slowly slip away.
                  </p>
                </div>

                <div className="bg-white p-6 rounded border border-gray-200" data-id="element-319">
                  <h4 className="font-bold text-[#1a4d2e] mb-2" data-id="element-320">
                    Option 2: Continue with expensive treatments.
                  </h4>
                  <p className="text-sm text-[#555]" data-id="element-321">
                    Keep going to physiotherapy twice a week at RM300 per
                    session (RM2,400+ per year). Hope your budget can sustain
                    it. Hope the clinic doesn't close. Hope you can keep taking
                    time off work for appointments.
                  </p>
                </div>

                <div className="bg-[#e8f5e9] p-6 rounded border border-[#4caf50]" data-id="element-322">
                  <h4 className="font-bold text-[#1a4d2e] mb-2" data-id="element-323">
                    Option 3: Try this risk-free for 90 days.
                  </h4>
                  <p className="text-sm text-[#555]" data-id="element-324">
                    Invest RM999 once. Use it every morning for 15 minutes. See
                    if your morning stiffness improves within 2 weeks. If it
                    works (like it has for 12,000+ other Malaysians), you've
                    solved your problem permanently for less than the cost of 4
                    physiotherapy sessions. If it doesn't work for you, you get
                    your money back.
                  </p>
                </div>
              </div>

              <div className="text-center mb-16" data-id="element-325">
                <a href="https://officiallumera.com/pages/lumera-3-1-knee-massager-1" target="_blank" rel="noopener noreferrer" className="inline-block bg-[#ff6b35] hover:bg-[#ff5722] text-white font-bold text-xl py-5 px-8 rounded-full shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 font-sans" data-id="element-326">
                  Yes, I Want To Try Lumera Gen-2™ Risk-Free
                  <span className="block text-sm font-normal mt-1 opacity-90" data-id="element-327">
                    RM999 | 90-Day Guarantee | Only 38 Units Left
                  </span>
                </a>

                <p className="mt-4 text-sm text-gray-500 font-sans" data-id="element-328">
                  Secure checkout | Free Malaysia shipping | Malaysian customer
                  support
                  <br data-id="element-329" />
                  Ships from Selangor warehouse within 24 hours
                </p>
              </div>
            </div>
          </article>

          {/* Sidebar Column */}
          <aside className="hidden lg:block lg:col-span-4 space-y-8" data-id="element-330">
            <div className="sticky top-4" data-id="element-331">
              <ArticleSidebar data-id="element-332" />
            </div>
          </aside>
        </div>

        {/* Footer */}
        <footer className="border-t-2 border-gray-200 pt-8 mt-12 text-xs text-gray-400 font-sans leading-relaxed text-center" data-id="element-333">
          <p className="mb-4" data-id="element-334">
            <strong data-id="element-335">Medical Disclaimer:</strong> This device is a wellness and
            recovery tool, not intended to diagnose, treat, cure, or prevent any
            medical condition. Results may vary by individual. If you have a
            serious medical condition, consult your healthcare provider before
            use. Clinical red light therapy has been studied in over 4,000
            peer-reviewed papers; specific results are not guaranteed for every
            user.
          </p>
          <p className="mb-4" data-id="element-336">
            <strong data-id="element-337">Customer Service:</strong> Malaysian support team available
            Mon-Sat, 9am-6pm MYT | Email: support@lumeraa.net
          </p>
          <p data-id="element-338">
            © 2026 Lumera Malaysia. All rights reserved. |{' '}
            <a href="#" className="underline" data-id="element-339">
              Privacy Policy
            </a>{' '}
            |{' '}
            <a href="#" className="underline" data-id="element-340">
              Terms of Service
            </a>{' '}
            |{' '}
            <a href="#" className="underline" data-id="element-341">
              Return Policy
            </a>
          </p>
        </footer>
      </main>
    </div>;
}